import { LinearGradient } from 'expo-linear-gradient';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Animated, {
  useAnimatedStyle,
  withTiming,
} from 'react-native-reanimated';
import ChevronIcon from '../../../../assets/images/landing/chevron-right.svg';
import DailyAlertsIcon from '../../../../assets/images/landing/daily-alerts.svg';
import MatchScoreIcon from '../../../../assets/images/landing/match-score.svg';
import PriceIntelIcon from '../../../../assets/images/landing/price-intel.svg';
import ScanDailyIcon from '../../../../assets/images/landing/scan-daily.svg';
import { useResponsive } from '../../../hooks/useResponsive';
import { COLORS } from '../../../styles/colors';
import env from '../../../utils/environment';
import { fontFamilies } from '../../../utils/fontLoader';
import logger from '../../../utils/logger';
import GradientText from '../../ui/GradientText';
import ResponsiveImage from '../../ui/ResponsiveImage';
import CarListingCard from '../ui/CarListingCard';

const CAROUSEL_DURATION = 5000;
const ANIMATION_DURATION = 700;

const featureIcons = {
  'scan-daily': ScanDailyIcon,
  'match-score': MatchScoreIcon,
  'price-intel': PriceIntelIcon,
  'daily-alerts': DailyAlertsIcon,
};

const AnimatedCarCard = memo(({ card, index, activeIndex, isMobile }) => {
  const isActive = index === activeIndex;
  const animatedStyle = useAnimatedStyle(() => {
    const active = index === activeIndex;
    return {
      opacity: withTiming(active ? 1 : 0, { duration: ANIMATION_DURATION }),
      transform: [
        { translateY: withTiming(active ? 0 : -60, { duration: ANIMATION_DURATION }) },
      ],
      position: index === 0 ? 'relative' : 'absolute',
      top: 0,
      left: 0,
      width: '100%',
    };
  });

  return (
    <Animated.View style={animatedStyle} pointerEvents={isActive ? 'auto' : 'none'}>
      <CarListingCard card={card} isMobile={isMobile} />
    </Animated.View>
  );
});

AnimatedCarCard.displayName = 'AnimatedCarCard';

const HeroSection = ({ content, onCtaPress }) => {
  const { isMobile } = useResponsive();
  const [activeIndex, setActiveIndex] = useState(0);
  const [dynamicCards, setDynamicCards] = useState(null);
  const fetchedRef = useRef(false);

  // Whether this variant uses a static hero image instead of the card carousel
  const useStaticImage = !!content.heroImage;

  // Fetch dynamic hero listings from CDN in the background (skip for static image variants)
  const fetchHeroListings = useCallback(async () => {
    if (fetchedRef.current || useStaticImage) return;
    fetchedRef.current = true;
    try {
      const response = await fetch(`${env.cdnBaseUrl}/landing/hero-listings.json`);
      if (!response.ok) throw new Error(`CDN returned ${response.status}`);
      const result = await response.json();
      if (result?.cards?.length > 0) {
        setDynamicCards(result.cards);
      }
    } catch (err) {
      logger.warn('Hero listings CDN fetch failed, using static fallback:', err?.message);
    }
  }, [useStaticImage]);

  useEffect(() => {
    fetchHeroListings();
  }, [fetchHeroListings]);

  // Use dynamic cards if available, otherwise fall back to static content
  const staticCards = content.heroCards || [];
  const rawCards = dynamicCards || staticCards;
  // Duplicate single card so carousel always has two slots to animate between
  const cards = rawCards.length === 1 ? [rawCards[0], rawCards[0]] : rawCards;

  useEffect(() => {
    if (cards.length < 2 || useStaticImage) return;

    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % cards.length);
    }, CAROUSEL_DURATION);

    return () => clearInterval(interval);
  }, [cards.length, useStaticImage]);

  return (
    <LinearGradient
      colors={['#0E264D', '#041635', '#020D25', '#040409']}
      locations={[0, 0.15, 0.45, 1]}
      style={[styles.heroContainer, isMobile && styles.heroContainerMobile, useStaticImage && styles.heroContainerStatic]}
    >
      <View style={[styles.contentContainer, isMobile && styles.contentContainerMobile]}>
        <View style={[styles.mainContent, isMobile && styles.mainContentMobile]}>
          {/* Text column */}
          <View style={[styles.textColumn, isMobile && styles.textColumnMobile]}>
            <GradientText
              style={[styles.titleLine1, isMobile && styles.titleLine1Mobile]}
              colors={['#FFFFFF 13.5%', '#E1EBF0 88.4%']}
              accessibilityRole="header"
              aria-level="1"
            >
              {content.titleLine1}
            </GradientText>
            <GradientText
              style={[styles.titleLine2, isMobile && styles.titleLine2Mobile]}
              colors={['#FFFFFF 13.5%', '#E1EBF0 88.4%']}
            >
              {content.titleLine2}
            </GradientText>

            <Text style={[styles.subtitle, isMobile && styles.subtitleMobile]}>
              {content.subtitle.split(/(\*\*.*?\*\*)/).map((part, i) =>
                part.startsWith('**') && part.endsWith('**') ? (
                  <Text key={i} style={styles.subtitleBold}>{part.slice(2, -2)}</Text>
                ) : (
                  part
                )
              )}
            </Text>

            <TouchableOpacity
              style={[styles.ctaButton, isMobile && styles.ctaButtonMobile]}
              onPress={onCtaPress}
              accessibilityRole="button"
            >
              <LinearGradient
                colors={['#1B60CA', '#0A47A4']}
                style={styles.ctaGradient}
                start={{ x: 0, y: 0 }}
                end={{ x: 0, y: 1 }}
              >
                <Text style={styles.ctaButtonText}>{content.cta.title}</Text>
                <View style={styles.ctaButtonIconWrapper}>
                  <ChevronIcon width={19.2} height={19.2} />
                </View>
              </LinearGradient>
            </TouchableOpacity>

            {/* Mobile: static hero image or card carousel inline */}
            {isMobile && useStaticImage && (
              <View style={styles.mobileHeroImageContainer}>
                <ResponsiveImage
                  sources={content.heroImage}
                  sizes="(max-width: 767px) 100vw, 537px"
                  style={styles.heroImage}
                  accessibilityLabel="Car buying illustration"
                  resizeMode="contain"
                  fetchpriority="high"
                />
              </View>
            )}
            {isMobile && !useStaticImage && cards.length > 0 && (
              <View style={styles.mobileCarouselContainer}>
                {cards.map((card, index) => (
                  <AnimatedCarCard
                    key={index}
                    card={card}
                    index={index}
                    activeIndex={activeIndex}
                    isMobile={isMobile}
                  />
                ))}
              </View>
            )}
          </View>

          {/* Desktop: static hero image or card carousel floating right */}
          {!isMobile && useStaticImage && (
            <View style={styles.heroImageContainer}>
              <ResponsiveImage
                sources={content.heroImage}
                sizes="(min-width: 768px) 500px, 100vw"
                style={styles.heroImage}
                accessibilityLabel="Car buying illustration"
                resizeMode="contain"
                fetchpriority="high"
              />
            </View>
          )}
          {!isMobile && !useStaticImage && cards.length > 0 && (
            <View style={styles.carouselContainer}>
              {cards.map((card, index) => (
                <AnimatedCarCard
                  key={index}
                  card={card}
                  index={index}
                  activeIndex={activeIndex}
                  isMobile={isMobile}
                />
              ))}
            </View>
          )}
        </View>

        {/* Features bar */}
        <View style={[styles.separatorContainer, isMobile && styles.separatorContainerMobile]}>
          <LinearGradient
            colors={['rgba(255,255,255,0.12)', 'transparent']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={styles.separator}
          />
        </View>

        <View style={[styles.featuresBar, isMobile && styles.featuresBarMobile]}>
          {content.featuresBar.features.map((feature) => {
            const Icon = featureIcons[feature.icon];
            return (
              <View
                key={feature.text}
                style={[styles.featureItem, isMobile && styles.featureItemMobile]}
              >
                {Icon && <Icon width={28} height={28} />}
                <Text style={[styles.featureText, isMobile && styles.featureTextMobile]}>
                  {feature.text}
                </Text>
              </View>
            );
          })}
        </View>
      </View>
    </LinearGradient>
  );
};

const styles = StyleSheet.create({
  heroContainer: {
    width: '100%',
    minHeight: 800,
    paddingTop: 110,
    alignItems: 'center',
    overflow: 'visible',
  },
  heroContainerMobile: {
    minHeight: 'auto',
    paddingTop: 80,
  },
  heroContainerStatic: {
    minHeight: 'auto',
  },
  contentContainer: {
    width: '100%',
    maxWidth: 1200,
    paddingHorizontal: 0,
    paddingBottom: 60,
  },
  contentContainerMobile: {
    paddingHorizontal: 24,
    paddingBottom: 40,
  },
  mainContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    width: '100%',
    gap: 40,
  },
  mainContentMobile: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: 0,
  },
  textColumn: {
    flex: 1,
    maxWidth: 900,
    paddingTop: 40,
  },
  textColumnMobile: {
    maxWidth: '100%',
    alignItems: 'center',
    paddingTop: 0,
  },
  titleLine1: {
    fontFamily: fontFamilies.satoshi.bold,
    fontSize: 32,
    lineHeight: 40,
    textAlign: 'left',
  },
  titleLine1Mobile: {
    fontSize: 24,
    lineHeight: 32,
    textAlign: 'center',
    marginTop: 24,
  },
  titleLine2: {
    fontFamily: fontFamilies.satoshi.bold,
    fontSize: 44,
    lineHeight: 54,
    textAlign: 'left',
    marginTop: 4,
  },
  titleLine2Mobile: {
    fontSize: 32,
    lineHeight: 40,
    textAlign: 'center',
    marginTop: 8,
  },
  subtitle: {
    fontFamily: fontFamilies.satoshi.light,
    fontSize: 20,
    lineHeight: 32,
    color: 'rgba(255,255,255,0.8)',
    textAlign: 'left',
    marginTop: 24,
    maxWidth: 900,
  },
  subtitleBold: {
    fontFamily: fontFamilies.satoshi.bold,
    color: '#FFFFFF',
  },
  subtitleMobile: {
    fontSize: 16,
    lineHeight: 24,
    textAlign: 'center',
    marginTop: 24,
  },
  ctaButton: {
    marginTop: 40,
    minWidth: 200,
    alignSelf: 'flex-start',
    borderRadius: 99,
    overflow: 'hidden',
    boxShadow: 'inset 0 8px 16px rgba(13,13,18,0.04)',
  },
  ctaButtonMobile: {
    alignSelf: 'center',
    marginTop: 32,
  },
  ctaGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    paddingVertical: 14,
    paddingLeft: 32,
    paddingRight: 12,
    borderRadius: 99,
  },
  ctaButtonText: {
    fontFamily: fontFamilies.satoshi.bold,
    fontSize: 14,
    color: COLORS.white,
    letterSpacing: -0.01,
  },
  ctaButtonIconWrapper: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: COLORS.white,
    justifyContent: 'center',
    alignItems: 'center',
  },
  heroImageContainer: {
    width: 537,
    height: 418,
    marginTop: 47,
  },
  mobileHeroImageContainer: {
    width: '100%',
    height: 300,
    marginTop: 32,
  },
  heroImage: {
    width: '100%',
    height: '100%',
  },
  carouselContainer: {
    width: 375,
    position: 'relative',
    marginTop: 20,
    overflow: 'visible',
    minHeight: 420,
  },
  mobileCarouselContainer: {
    width: '100%',
    position: 'relative',
    marginTop: 32,
    overflow: 'visible',
    minHeight: 380,
  },
  separatorContainer: {
    width: '100%',
    marginTop: 60,
    boxShadow: '0 -80px 72px rgba(0,0,0,0.32)',
  },
  separatorContainerMobile: {
    marginTop: 40,
  },
  separator: {
    width: '100%',
    height: 1,
  },
  featuresBar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    paddingTop: 32,
  },
  featuresBarMobile: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 16,
    paddingTop: 24,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  featureItemMobile: {
    width: '45%',
    justifyContent: 'flex-start',
  },
  featureText: {
    fontFamily: fontFamilies.satoshi.medium,
    fontSize: 14,
    color: COLORS.white,
  },
  featureTextMobile: {
    fontSize: 12,
  },
});

export default HeroSection;
